import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ClipboardCheck, Loader2, User, Workflow } from "lucide-react";
import { api } from "../../api/client";
import FieldPageChrome from "../../components/field/FieldPageChrome";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { unwrap, unwrapArray } from "../../lib/api-envelope";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import {
  DASH,
  GradeBadge,
  ItemBadge,
  TypeChip,
  TYPE_LABEL,
  gradeSkin,
  normalizeInspection,
  type Inspection,
  type InspectionApiRow,
} from "../../components/rentals/InspectionBits";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import { RentalInspectionPhotoList } from "../../components/rentals/RentalInspectionPhotos";

/**
 * One filed rental inspection, /rentals/inspections/:id (#815 Inspections).
 * Read-only: a filed inspection is never re-submitted. It was the right-hand
 * panel of the Inspections split pane.
 *
 * GET /inspections/:id is `rental_inspections.*` plus the asset's number and
 * name; the readings are the register's (InspectionBits.normalizeInspection),
 * so a grade it cannot read is a dash, never a Pass. The order (GET
 * /orders/:id, for its number) and the work order a Fail opened link to their
 * pages. A post-return is shown beside the order's newest pre-delivery of the
 * same asset (GET /inspections?asset_id=&order_id=).
 */
/**
 * `surface="field"`: an inspection opened from the field Inspections tile
 * (fe#815). The casing field header and back arrow in place of the office band.
 * The band's When and Inspected by figures move into the record panel, and the
 * order and work-order links (office routes) name their target without linking.
 */
export default function RentalInspectionDetail({ surface = "office" }: { surface?: "office" | "field" } = {}) {
  const isField = surface === "field";
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();

  const query = useQuery({
    queryKey: ["rentals", "inspections", "one", id, timeZone],
    queryFn: async () => {
      const raw = unwrap<InspectionApiRow>((await api.rentals.inspections.get(id)).data);
      return raw && raw.id ? normalizeInspection(raw, timeZone) : null;
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const ins = failure ? null : query.data ?? null;
  const loading = query.isLoading && !failure;

  const orderQuery = useQuery({
    queryKey: ["rentals-order", ins?.order_id],
    queryFn: async () => unwrap<{ order_number?: string | null }>((await api.rentals.orders.get(ins!.order_id)).data),
    enabled: !!ins?.order_id,
  });
  const orderNumber = orderQuery.data?.order_number ?? null;

  // The newest (the list is newest first) pre-delivery of this asset on this order.
  const companionQuery = useQuery({
    queryKey: ["rentals", "inspections", "pre-for", ins?.asset_id, ins?.order_id, timeZone],
    queryFn: async () =>
      unwrapArray<InspectionApiRow>((await api.rentals.inspections.list({ asset_id: ins!.asset_id, order_id: ins!.order_id })).data)
        .map((row) => normalizeInspection(row, timeZone))
        .find((i) => i.type === "pre_delivery") ?? null,
    enabled: !!ins && ins.type === "post_return" && !!ins.order_id,
  });
  const companion: Inspection | null = companionQuery.data ?? null;

  const body = (
    <>
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading inspection" />
            </div>
          ) : !ins ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this inspection" />
              ) : (
                <DhEmpty>This inspection does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              <DhPanel eyebrow="Inspection" title="Inspection record" testId="inspection-detail">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec label="Asset" value={`${ins.asset_name} · ${ins.asset_tag}`} />
                  <DhSpec label="Type" value={<TypeChip type={ins.type} />} />
                  <DhSpec label="Overall" value={<GradeBadge grade={ins.overall} />} />
                  {isField && <DhSpec label="When" value={ins.when} />}
                  {isField && <DhSpec label="Inspected by" value={ins.performed_by} />}
                  <DhSpec
                    label="Order"
                    value={
                      ins.order_id ? (
                        isField ? (
                          orderNumber ?? DASH
                        ) : (
                          <Link className="underline underline-offset-2" to={`/rentals/orders/${encodeURIComponent(ins.order_id)}`}>
                            {orderNumber ?? "Open order"}
                          </Link>
                        )
                      ) : (
                        DASH
                      )
                    }
                  />
                </dl>
                {ins.work_order_id && (
                  <p data-testid="inspection-work-order" className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    This Fail opened a work order and put the asset in maintenance.
                    {!isField && (
                      <>
                        {" "}
                        <Link className="font-semibold underline underline-offset-2" to={`/rentals/maintenance/${encodeURIComponent(ins.work_order_id)}`}>
                          Open work order
                        </Link>
                      </>
                    )}
                  </p>
                )}
                {!ins.structured && (
                  <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">Recorded in the older format, before checklists by equipment family.</p>
                )}
              </DhPanel>

              <DhPanel eyebrow="Uploads" title="Photos" testId="inspection-photos">
                <div className="p-4">
                  <RentalInspectionPhotoList inspectionId={ins.id} keys={ins.photo_keys} />
                </div>
              </DhPanel>

              <DhPanel eyebrow="Checklist" title="Items" testId="inspection-items">
                {ins.items.length === 0 ? (
                  <DhEmpty>No item results were recorded on this inspection.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                          <th className="px-4 py-3">Item</th>
                          <th className="w-32 px-4 py-3">Result</th>
                          <th className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ins.items.map((item) => (
                          <tr key={item.key} className="border-t border-fieldiq-border">
                            <td className="px-4 py-2 text-sm font-bold text-fieldiq-text">{item.label}</td>
                            <td className="px-4 py-2"><ItemBadge result={item.result} /></td>
                            <td className="px-4 py-2 text-sm text-fieldiq-text">{item.notes || DASH}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              {companion && (
                <DhPanel eyebrow="Before and after" title="Pre-Delivery vs Post-Return" testId="inspection-compare">
                  <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                    {[
                      { title: `Pre-Delivery (${companion.when})`, ins: companion, to: companion.id },
                      { title: `Post-Return (${ins.when})`, ins, to: null },
                    ].map((col) => (
                      <div key={col.title}>
                        <p className="mb-2 text-xs font-bold uppercase text-fieldiq-muted">
                          {col.to ? (
                            <Link className="underline underline-offset-2" to={`/rentals/inspections/${encodeURIComponent(col.to)}`}>{col.title}</Link>
                          ) : (
                            col.title
                          )}
                        </p>
                        <div className="space-y-1">
                          {col.ins.items.map((item) => (
                            <div key={item.key} className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-fieldiq-muted">{item.label}</span>
                              <ItemBadge result={item.result} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </DhPanel>
              )}
            </>
          )}
    </>
  );

  if (isField) {
    return (
      <FieldPageChrome testId="rental-inspection-page" title={ins ? ins.asset_name : loading ? "Loading…" : "Inspection"} onBack={() => navigate("/rentals/inspections")}>
        {body}
      </FieldPageChrome>
    );
  }

  return (
    <div data-testid="rental-inspection-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={ins ? `Inspection · ${ins.type ? TYPE_LABEL[ins.type] : DASH}` : "Inspection"}
        title={ins ? ins.asset_name : loading ? "Loading…" : "Inspection"}
        onBack={() => navigate("/rentals/inspections")}
        backLabel="Back to Inspections"
        stats={
          !ins
            ? undefined
            : [
                { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: "Overall", sub: "As filed", tone: "neutral", value: gradeSkin(ins.overall).label },
                { icon: <Workflow className="size-4" aria-hidden="true" />, label: "Type", sub: ins.asset_tag, tone: "info", value: ins.type ? TYPE_LABEL[ins.type] : DASH },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "When", sub: "Tenant time", tone: "neutral", value: ins.when },
                { icon: <User className="size-4" aria-hidden="true" />, label: "Inspected by", sub: "Inspector", tone: "neutral", value: ins.performed_by },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {body}
        </div>
      </section>
    </div>
  );
}
